import { useState, useEffect } from "react";
import {
  getAllSavedResources,
  saveResources,
  removeResources,
} from "../../utils/api";
import { SaveIcon, SaveIconActive } from "../../common/icons";

interface SaveButtonProps {
  resourceId: number;
  resourceType?: "scholarship" | "course" | "institute";
}

export default function SaveButton({
  resourceId,
  resourceType = "scholarship",
}: SaveButtonProps) {
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function checkIfSaved() {
      try {
        if (!resourceId) return;
        setIsLoading(true);

        const response = await getAllSavedResources(resourceType);
        let savedIds: number[] = [];

        if (resourceType === "scholarship" && response.scholarships) {
          savedIds = response.scholarships.map(
            (item: { scholarship: { id: number } }) => item.scholarship.id,
          );
        } else if (resourceType === "course" && response.courses) {
          savedIds = response.courses.map(
            (item: { course: { id: number } }) => item.course.id,
          );
        } else if (resourceType === "institute" && response.institutes) {
          savedIds = response.institutes.map(
            (item: { institute: { id: number } }) => item.institute.id,
          );
        }

        setIsSaved(savedIds.includes(Number(resourceId)));
      } catch (error) {
        console.error("Error checking saved status:", error);
      } finally {
        setIsLoading(false);
      }
    }

    checkIfSaved();
  }, [resourceId, resourceType]);

  async function toggleSave() {
    try {
      if (!resourceId) return;

      if (isSaved) {
        await removeResources(resourceId, resourceType);
        setIsSaved(false);
      } else {
        await saveResources(resourceId, resourceType);
        setIsSaved(true);
      }
    } catch (error) {
      console.error("Error toggling save status:", error);
    }
  }

  return (
    <button
      onClick={toggleSave}
      disabled={isLoading}
      className="group rounded-full border border-primary relative"
      aria-label={isSaved ? "Remove from saved" : "Save for later"}
    >
      {isSaved ? <SaveIconActive /> : <SaveIcon className="m-2" />}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block pointer-events-none">
        <div className="bg-gray-800 text-white px-2 py-1 rounded text-sm whitespace-nowrap">
          {isSaved ? "Saved" : "Save"}
        </div>
      </div>
    </button>
  );
}
